import React, { useState, useEffect, useCallback, Component } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart, Bar, AreaChart, Area, XAxis, YAxis, Tooltip,
  ResponsiveContainer, CartesianGrid
} from 'recharts';
import {
  RefreshCw, Download, Users, TrendingUp, CreditCard,
  DollarSign, AlertCircle, ChevronLeft, ChevronRight,
  X, AlertTriangle, Shield, ShieldCheck, Activity, PiggyBank
} from 'lucide-react';

import { API_URL } from './config';
import { AdminCommissionOverrides } from './AdminCommissionOverrides';

/** Resolve session token: prop first (available right after login). */
const resolveToken = (propToken) => propToken || null;

// =============================================================================
// ERROR BOUNDARY
// =============================================================================
class AdminErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, errorInfo) {
    console.error('[AdminDashboard] React Error:', error, errorInfo);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-[#0f1117] flex items-center justify-center p-6">
          <div className="bg-[#1a1d27] rounded-2xl border border-red-500/30 p-8 max-w-lg text-center">
            <AlertTriangle className="w-14 h-14 text-red-400 mx-auto mb-4" />
            <h1 className="text-xl font-bold text-white mb-2">Something went wrong</h1>
            <p className="text-gray-400 mb-6 text-sm">The admin dashboard encountered an error.</p>
            <button
              data-testid="error-boundary-refresh-btn"
              onClick={() => window.location.reload()}
              className="px-5 py-2.5 bg-red-500 text-white rounded-lg hover:bg-red-600 transition text-sm font-medium"
            >
              Refresh Page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// =============================================================================
// UTILS
// =============================================================================
const formatCurrency = (amount) => {
  if (amount === null || amount === undefined || amount === '') return '$0.00';
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
  } catch {
    return '$0.00';
  }
};

const formatDate = (dateStr) => {
  if (!dateStr) return '\u2014';
  try {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  } catch {
    return dateStr;
  }
};

const formatShortDate = (dateStr) => {
  if (!dateStr) return '';
  try {
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch {
    return dateStr;
  }
};

// =============================================================================
// LOADING SCREEN
// =============================================================================
const LoadingScreen = ({ message = 'Loading...' }) => (
  <div data-testid="admin-loading-screen" className="min-h-screen bg-[#0f1117] flex items-center justify-center p-6">
    <div className="text-center">
      <div className="relative w-16 h-16 mx-auto">
        <div className="w-16 h-16 border-4 border-[#2a2d3a] rounded-full"></div>
        <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin absolute top-0 left-0"></div>
      </div>
      <p className="text-gray-400 mt-6 font-medium text-sm">{message}</p>
    </div>
  </div>
);

// =============================================================================
// NOT AUTHORIZED SCREEN
// =============================================================================
const NotAuthorizedScreen = ({ userEmail }) => (
  <div data-testid="admin-not-authorized" className="min-h-screen bg-[#0f1117] flex items-center justify-center p-6">
    <div className="bg-[#1a1d27] rounded-2xl border border-[#2a2d3a] p-8 max-w-md text-center">
      <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-5">
        <Shield className="w-8 h-8 text-red-400" />
      </div>
      <h1 className="text-xl font-bold text-white mb-2">Access Denied</h1>
      <p className="text-gray-400 mb-5 text-sm">You don't have permission to access the admin dashboard.</p>
      {userEmail && (
        <p className="text-xs text-gray-500 mb-5">Logged in as: {userEmail}</p>
      )}
      <a
        href="/dashboard"
        data-testid="go-to-dashboard-btn"
        className="block w-full px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition text-sm font-medium"
      >
        Go to Dashboard
      </a>
    </div>
  </div>
);

// =============================================================================
// ERROR BANNER
// =============================================================================
const ErrorBanner = ({ error, onDismiss }) => {
  if (!error) return null;
  return (
    <div data-testid="error-banner" className="bg-red-500/10 border border-red-500/20 rounded-xl p-4 mb-6 flex items-start gap-3">
      <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
      <div className="flex-1">
        <p className="text-red-300 font-medium text-sm">Error loading data</p>
        <p className="text-red-400/70 text-xs mt-1">{error}</p>
      </div>
      {onDismiss && (
        <button onClick={onDismiss} data-testid="dismiss-error-btn" className="text-red-400/50 hover:text-red-400">
          <X className="w-4 h-4" />
        </button>
      )}
    </div>
  );
};

// =============================================================================
// KPI CARD
// =============================================================================
const COLOR_MAP = {
  blue:    { bg: 'bg-blue-500/10',    text: 'text-blue-400',    border: 'border-blue-500/20' },
  purple:  { bg: 'bg-purple-500/10',  text: 'text-purple-400',  border: 'border-purple-500/20' },
  green:   { bg: 'bg-emerald-500/10', text: 'text-emerald-400', border: 'border-emerald-500/20' },
  emerald: { bg: 'bg-emerald-500/10', text: 'text-emerald-400', border: 'border-emerald-500/20' },
  orange:  { bg: 'bg-orange-500/10',  text: 'text-orange-400',  border: 'border-orange-500/20' },
  teal:    { bg: 'bg-teal-500/10',    text: 'text-teal-400',    border: 'border-teal-500/20' },
  cyan:    { bg: 'bg-cyan-500/10',    text: 'text-cyan-400',    border: 'border-cyan-500/20' },
};

const KPICard = ({ title, value, icon: Icon, color = 'blue', isCurrency, loading, error, testId, subtitle }) => {
  const c = COLOR_MAP[color] || COLOR_MAP.blue;
  return (
    <div data-testid={testId} className={`bg-[#1a1d27] rounded-xl border ${c.border} p-5 transition hover:border-opacity-60`}>
      <div className="flex items-center justify-between">
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">{title}</p>
          {loading ? (
            <div className="h-8 w-28 bg-[#2a2d3a] animate-pulse rounded mt-2" />
          ) : error ? (
            <p className="text-red-400 text-sm mt-2">Error</p>
          ) : (
            <>
              <p className="text-2xl font-bold text-white mt-1 truncate">
                {isCurrency ? formatCurrency(value) : typeof value === 'number' ? value.toLocaleString() : value ?? '\u2014'}
              </p>
              {subtitle && (
                <p className="text-xs text-orange-400 mt-0.5">{subtitle}</p>
              )}
            </>
          )}
        </div>
        <div className={`p-3 rounded-xl ${c.bg} flex-shrink-0 ml-3`}>
          <Icon className={`w-5 h-5 ${c.text}`} />
        </div>
      </div>
    </div>
  );
};

// =============================================================================
// CHART CARD (using recharts)
// =============================================================================
const ChartTooltip = ({ active, payload, label, isCurrency }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="bg-[#1a1d27] border border-[#2a2d3a] rounded-lg px-3 py-2 shadow-xl">
      <p className="text-xs text-gray-400 mb-1">{formatShortDate(label)}</p>
      {payload.map((entry, i) => (
        <p key={i} className="text-sm font-medium" style={{ color: entry.color }}>
          {entry.name}: {isCurrency ? formatCurrency(entry.value) : entry.value}
        </p>
      ))}
    </div>
  );
};

const ChartCard = ({ title, data, dataKey, color, secondaryKey, secondaryColor, isCurrency, testId, type = 'bar' }) => {
  const safeData = Array.isArray(data) ? data : [];
  const hasData = safeData.some(d => (d?.[dataKey] || 0) > 0 || (secondaryKey && (d?.[secondaryKey] || 0) > 0));

  return (
    <div data-testid={testId} className="bg-[#1a1d27] rounded-xl border border-[#2a2d3a] p-5">
      <h3 className="text-sm font-semibold text-white mb-4">{title}</h3>
      {!hasData ? (
        <div className="h-48 flex items-center justify-center text-gray-500 text-sm">No data yet</div>
      ) : (
        <ResponsiveContainer width="100%" height={200}>
          {type === 'area' ? (
            <AreaChart data={safeData}>
              <defs>
                <linearGradient id={`grad-${dataKey}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={color} stopOpacity={0.3} />
                  <stop offset="95%" stopColor={color} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#2a2d3a" />
              <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#6b7280' }} tickFormatter={formatShortDate} interval={Math.floor(safeData.length / 6)} />
              <YAxis tick={{ fontSize: 10, fill: '#6b7280' }} width={45} tickFormatter={isCurrency ? (v) => `$${v}` : undefined} />
              <Tooltip content={<ChartTooltip isCurrency={isCurrency} />} />
              <Area type="monotone" dataKey={dataKey} name={dataKey} stroke={color} fill={`url(#grad-${dataKey})`} strokeWidth={2} />
              {secondaryKey && (
                <Area type="monotone" dataKey={secondaryKey} name={secondaryKey} stroke={secondaryColor} fill="transparent" strokeWidth={1.5} strokeDasharray="4 2" />
              )}
            </AreaChart>
          ) : (
            <BarChart data={safeData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#2a2d3a" />
              <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#6b7280' }} tickFormatter={formatShortDate} interval={Math.floor(safeData.length / 6)} />
              <YAxis tick={{ fontSize: 10, fill: '#6b7280' }} width={45} tickFormatter={isCurrency ? (v) => `$${v}` : undefined} />
              <Tooltip content={<ChartTooltip isCurrency={isCurrency} />} />
              <Bar dataKey={dataKey} name={dataKey} fill={color} radius={[3, 3, 0, 0]} />
              {secondaryKey && (
                <Bar dataKey={secondaryKey} name={secondaryKey} fill={secondaryColor} radius={[3, 3, 0, 0]} />
              )}
            </BarChart>
          )}
        </ResponsiveContainer>
      )}
    </div>
  );
};

// =============================================================================
// DATA TABLE
// =============================================================================
const DataTable = ({
  columns, data, loading, error, total, limit, offset,
  onPageChange, onExport, exportLabel = 'Export CSV', testId
}) => {
  const safeData = Array.isArray(data) ? data : [];
  const safeTotal = typeof total === 'number' ? total : 0;
  const totalPages = Math.ceil(safeTotal / limit) || 1;
  const currentPage = Math.floor(offset / limit) + 1;

  if (error) {
    return (
      <div data-testid={`${testId}-error`} className="bg-red-500/10 border border-red-500/20 rounded-xl p-6 text-center">
        <AlertCircle className="w-8 h-8 text-red-400 mx-auto mb-2" />
        <p className="text-red-300 font-medium text-sm">Failed to load data</p>
        <p className="text-red-400/70 text-xs mt-1">{error}</p>
      </div>
    );
  }

  return (
    <div data-testid={testId}>
      <div className="flex justify-between items-center mb-4">
        <p className="text-xs text-gray-500">
          {loading ? 'Loading...' : `${Math.min(offset + 1, safeTotal)}\u2013${Math.min(offset + limit, safeTotal)} of ${safeTotal}`}
        </p>
        {onExport && (
          <button
            data-testid={`${testId}-export-btn`}
            onClick={onExport}
            disabled={loading}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-[#2a2d3a] hover:bg-[#353845] text-gray-300 rounded-lg transition disabled:opacity-50"
          >
            <Download className="w-3.5 h-3.5" />
            {exportLabel}
          </button>
        )}
      </div>

      <div className="overflow-x-auto rounded-lg border border-[#2a2d3a]">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[#2a2d3a] bg-[#13151c]">
              {columns.map((col, i) => (
                <th key={i} className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  {col.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              [...Array(5)].map((_, i) => (
                <tr key={i} className="border-b border-[#1e2130]">
                  {columns.map((_, j) => (
                    <td key={j} className="py-3 px-4">
                      <div className="h-4 bg-[#2a2d3a] animate-pulse rounded w-20" />
                    </td>
                  ))}
                </tr>
              ))
            ) : safeData.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="py-10 text-center text-gray-500 text-sm">
                  No data found
                </td>
              </tr>
            ) : (
              safeData.map((row, i) => (
                <tr key={i} className="border-b border-[#1e2130] hover:bg-[#1e2130]/50 transition-colors">
                  {columns.map((col, j) => (
                    <td key={j} className="py-3 px-4 text-gray-300 text-xs whitespace-nowrap">
                      {col.render ? col.render(row) : (row?.[col.key] ?? '\u2014')}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex justify-center items-center gap-3 mt-4">
          <button
            data-testid={`${testId}-prev-page-btn`}
            onClick={() => onPageChange(Math.max(0, offset - limit))}
            disabled={currentPage === 1 || loading}
            className="p-2 rounded-lg bg-[#2a2d3a] hover:bg-[#353845] disabled:opacity-30 disabled:cursor-not-allowed transition"
          >
            <ChevronLeft className="w-4 h-4 text-gray-400" />
          </button>
          <span className="text-xs text-gray-500">Page {currentPage} of {totalPages}</span>
          <button
            data-testid={`${testId}-next-page-btn`}
            onClick={() => onPageChange(offset + limit)}
            disabled={currentPage === totalPages || loading}
            className="p-2 rounded-lg bg-[#2a2d3a] hover:bg-[#353845] disabled:opacity-30 disabled:cursor-not-allowed transition"
          >
            <ChevronRight className="w-4 h-4 text-gray-400" />
          </button>
        </div>
      )}
    </div>
  );
};

// =============================================================================
// FILTERS
// =============================================================================
const Filters = ({ filters, values, onChange, onClear, testId }) => (
  <div data-testid={testId} className="flex flex-wrap gap-3 items-center mb-4">
    {filters.map((filter) => (
      <div key={filter.key} className="flex items-center gap-2">
        <label className="text-xs text-gray-500">{filter.label}:</label>
        {filter.type === 'select' ? (
          <select
            data-testid={`${testId}-${filter.key}`}
            value={values?.[filter.key] || ''}
            onChange={(e) => onChange(filter.key, e.target.value)}
            className="text-xs bg-[#1a1d27] border border-[#2a2d3a] text-gray-300 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            <option value="">All</option>
            {filter.options?.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        ) : (
          <input
            type={filter.type || 'text'}
            data-testid={`${testId}-${filter.key}`}
            value={values?.[filter.key] || ''}
            onChange={(e) => onChange(filter.key, e.target.value)}
            placeholder={filter.placeholder}
            className="text-xs bg-[#1a1d27] border border-[#2a2d3a] text-gray-300 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        )}
      </div>
    ))}
    {values && Object.values(values).some(v => v) && (
      <button
        data-testid={`${testId}-clear-btn`}
        onClick={onClear}
        className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-300 transition"
      >
        <X className="w-3.5 h-3.5" />
        Clear
      </button>
    )}
  </div>
);

// =============================================================================
// STATUS BADGE
// =============================================================================
const StatusBadge = ({ status }) => {
  const styles = {
    completed:  'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
    active:     'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
    cancelling: 'bg-orange-500/10 text-orange-400 border-orange-500/20',
    pending:    'bg-yellow-500/10 text-yellow-400 border-yellow-500/20',
    failed:     'bg-red-500/10 text-red-400 border-red-500/20',
    cancelled:  'bg-red-500/10 text-red-400 border-red-500/20',
    canceled:   'bg-red-500/10 text-red-400 border-red-500/20',
    past_due:   'bg-orange-500/10 text-orange-400 border-orange-500/20',
    expired:    'bg-gray-500/10 text-gray-400 border-gray-500/20',
  };
  const labels = {
    cancelling: 'Cancelling',
    cancelled: 'Cancelled',
    canceled: 'Cancelled',
    past_due: 'Past Due',
  };
  return (
    <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-medium border ${styles[status] || styles.expired}`}>
      {labels[status] || status || '\u2014'}
    </span>
  );
};

// =============================================================================
// MAIN ADMIN DASHBOARD
// =============================================================================
const AdminDashboardContent = ({ user, token: propToken }) => {
  const navigate = useNavigate();
  const token = resolveToken(propToken);

  // Access control state
  const [accessState, setAccessState] = useState({
    checking: true, isLoggedIn: false, isAdmin: false, userEmail: null, error: null
  });

  // Dashboard state
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);

  const [metrics, setMetrics] = useState(null);
  const [metricsError, setMetricsError] = useState(null);

  const [transactions, setTransactions] = useState({ data: [], total: 0 });
  const [transactionsError, setTransactionsError] = useState(null);
  const [transactionsLoading, setTransactionsLoading] = useState(false);

  const [creators, setCreators] = useState({ data: [], total: 0 });
  const [creatorsError, setCreatorsError] = useState(null);
  const [creatorsLoading, setCreatorsLoading] = useState(false);

  const [subscriptions, setSubscriptions] = useState({ data: [], total: 0 });
  const [subscriptionsError, setSubscriptionsError] = useState(null);
  const [subscriptionsLoading, setSubscriptionsLoading] = useState(false);

  const [allUsers, setAllUsers] = useState({ data: [], total: 0 });
  const [allUsersError, setAllUsersError] = useState(null);
  const [allUsersLoading, setAllUsersLoading] = useState(false);

  const [charts, setCharts] = useState({ transactions: [], users: [], subscriptions: [] });

  const [txOffset, setTxOffset] = useState(0);
  const [creatorsOffset, setCreatorsOffset] = useState(0);
  const [subsOffset, setSubsOffset] = useState(0);
  const [usersOffset, setUsersOffset] = useState(0);

  const [txFilters, setTxFilters] = useState({ status: 'completed' });
  const [creatorsFilters, setCreatorsFilters] = useState({});
  const [subsFilters, setSubsFilters] = useState({});

  const limit = 50;

  // ===========================================================================
  // ACCESS CHECK — calls /api/me first, then /api/admin/check
  // ===========================================================================
  useEffect(() => {
    const verifyAccess = async () => {
      const sessionToken = resolveToken(propToken);
      if (!sessionToken) {
        navigate('/?login=true&next=/admin');
        return;
      }

      const authHeaders = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${sessionToken}`
      };

      try {
        // Step 1: Call /api/me to confirm session is valid
        const meRes = await fetch(`${API_URL}/api/me`, {
          headers: authHeaders,
          credentials: 'include'
        });
        if (meRes.status === 401) { navigate('/?login=true&next=/admin'); return; }
        if (!meRes.ok) {
          setAccessState({ checking: false, isLoggedIn: false, isAdmin: false, userEmail: null, error: 'Failed to verify session' });
          return;
        }
        const userData = await meRes.json();
        const email = userData?.email || null;

        // Step 2: Call /api/admin/check
        const adminRes = await fetch(`${API_URL}/api/admin/check`, {
          headers: authHeaders,
          credentials: 'include'
        });
        if (adminRes.status === 403) {
          setAccessState({ checking: false, isLoggedIn: true, isAdmin: false, userEmail: email, error: null });
          return;
        }
        if (!adminRes.ok) {
          setAccessState({ checking: false, isLoggedIn: true, isAdmin: false, userEmail: email, error: 'Failed to verify admin access' });
          return;
        }
        const adminData = await adminRes.json();
        setAccessState({ checking: false, isLoggedIn: true, isAdmin: adminData.is_admin === true, userEmail: email, error: null });
      } catch (err) {
        console.error('[Admin] Access verification failed:', err);
        setAccessState({ checking: false, isLoggedIn: false, isAdmin: false, userEmail: null, error: 'Network error \u2014 please try again' });
      }
    };
    verifyAccess();
  }, [propToken, navigate]);

  // ===========================================================================
  // FETCH HELPER
  // ===========================================================================
  const fetchWithAuth = useCallback(async (endpoint, params = {}) => {
    const qs = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== '' && v !== undefined && v !== null)
    ).toString();
    const url = `${API_URL}${endpoint}${qs ? `?${qs}` : ''}`;
    const headers = { 'Content-Type': 'application/json' };
    const sessionToken = resolveToken(propToken);
    if (sessionToken) headers['Authorization'] = `Bearer ${sessionToken}`;
    const res = await fetch(url, { headers, credentials: 'include' });
    if (res.status === 401) throw new Error('Session expired \u2014 please log in again');
    if (res.status === 403) throw new Error('Not authorized');
    if (res.status === 503) {
      const d = await res.json().catch(() => ({}));
      throw new Error(d.detail || 'Service unavailable');
    }
    if (!res.ok) {
      const d = await res.json().catch(() => ({}));
      throw new Error(typeof d.detail === 'string' ? d.detail : JSON.stringify(d.detail) || `HTTP ${res.status}`);
    }
    return res.json();
  }, [propToken]);

  // ===========================================================================
  // DATA LOADERS
  // ===========================================================================
  const loadMetrics = useCallback(async () => {
    setMetricsError(null);
    try {
      const data = await fetchWithAuth('/api/admin/metrics');
      setMetrics(data?.kpis || null);
    } catch (err) { setMetricsError(err.message); }
  }, [fetchWithAuth]);

  const loadCharts = useCallback(async () => {
    try {
      const [txChart, usersChart, subsChart] = await Promise.all([
        fetchWithAuth('/api/admin/charts/transactions', { days: 30 }).catch(() => ({ data: [] })),
        fetchWithAuth('/api/admin/charts/users', { days: 30 }).catch(() => ({ data: [] })),
        fetchWithAuth('/api/admin/charts/subscriptions', { days: 30 }).catch(() => ({ data: [] }))
      ]);
      setCharts({
        transactions: txChart?.data || [],
        users: usersChart?.data || [],
        subscriptions: subsChart?.data || []
      });
    } catch (err) { console.error('Charts error:', err); }
  }, [fetchWithAuth]);

  const loadTransactions = useCallback(async () => {
    setTransactionsLoading(true);
    setTransactionsError(null);
    try {
      const data = await fetchWithAuth('/api/admin/transactions', { ...txFilters, limit, offset: txOffset });
      setTransactions({ data: data?.data || [], total: data?.total || 0 });
    } catch (err) {
      setTransactionsError(err.message);
      setTransactions({ data: [], total: 0 });
    } finally { setTransactionsLoading(false); }
  }, [fetchWithAuth, txFilters, txOffset]);

  const loadCreators = useCallback(async () => {
    setCreatorsLoading(true);
    setCreatorsError(null);
    try {
      const data = await fetchWithAuth('/api/admin/creators', { ...creatorsFilters, limit, offset: creatorsOffset });
      setCreators({ data: data?.data || [], total: data?.total || 0 });
    } catch (err) {
      setCreatorsError(err.message);
      setCreators({ data: [], total: 0 });
    } finally { setCreatorsLoading(false); }
  }, [fetchWithAuth, creatorsFilters, creatorsOffset]);

  const loadSubscriptions = useCallback(async () => {
    setSubscriptionsLoading(true);
    setSubscriptionsError(null);
    try {
      const data = await fetchWithAuth('/api/admin/subscriptions', { ...subsFilters, limit, offset: subsOffset });
      setSubscriptions({ data: data?.data || [], total: data?.total || 0 });
    } catch (err) {
      setSubscriptionsError(err.message);
      setSubscriptions({ data: [], total: 0 });
    } finally { setSubscriptionsLoading(false); }
  }, [fetchWithAuth, subsFilters, subsOffset]);

  const loadAllUsers = useCallback(async () => {
    setAllUsersLoading(true);
    setAllUsersError(null);
    try {
      const data = await fetchWithAuth('/api/admin/users', { limit, offset: usersOffset });
      setAllUsers({ data: data?.data || [], total: data?.total || 0 });
    } catch (err) {
      setAllUsersError(err.message);
      setAllUsers({ data: [], total: 0 });
    } finally { setAllUsersLoading(false); }
  }, [fetchWithAuth, usersOffset]);

  // Load data only after access verified
  useEffect(() => {
    if (!accessState.checking && accessState.isAdmin) {
      const loadAll = async () => {
        setLoading(true);
        await Promise.all([loadMetrics(), loadCharts(), loadTransactions(), loadCreators(), loadSubscriptions(), loadAllUsers()]);
        setLastUpdated(new Date());
        setLoading(false);
      };
      loadAll();
    }
  }, [accessState.checking, accessState.isAdmin]); // eslint-disable-line

  useEffect(() => { if (!loading && accessState.isAdmin) loadTransactions(); }, [txFilters, txOffset]); // eslint-disable-line
  useEffect(() => { if (!loading && accessState.isAdmin) loadCreators(); }, [creatorsFilters, creatorsOffset]); // eslint-disable-line
  useEffect(() => { if (!loading && accessState.isAdmin) loadSubscriptions(); }, [subsFilters, subsOffset]); // eslint-disable-line
  useEffect(() => { if (!loading && accessState.isAdmin) loadAllUsers(); }, [usersOffset]); // eslint-disable-line

  const handleRefresh = async () => {
    if (!accessState.isAdmin) return;
    setLoading(true);
    await Promise.all([loadMetrics(), loadCharts(), loadTransactions(), loadCreators(), loadSubscriptions()]);
    setLastUpdated(new Date());
    setLoading(false);
  };

  const handleExport = async (type) => {
    try {
      const sessionToken = resolveToken(propToken);
      const res = await fetch(`${API_URL}/api/admin/export/${type}`, {
        headers: sessionToken ? { 'Authorization': `Bearer ${sessionToken}` } : {},
        credentials: 'include'
      });
      if (!res.ok) throw new Error(`Export failed: HTTP ${res.status}`);
      const blob = await res.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `${type}_${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      alert(`Export failed: ${err.message}`);
    }
  };

  // ===========================================================================
  // ACCESS GATES
  // ===========================================================================
  if (accessState.checking) return <LoadingScreen message="Verifying access..." />;
  if (accessState.isLoggedIn && !accessState.isAdmin) return <NotAuthorizedScreen userEmail={accessState.userEmail} />;
  if (accessState.error) {
    return (
      <div className="min-h-screen bg-[#0f1117] flex items-center justify-center p-6">
        <div data-testid="admin-access-error" className="bg-[#1a1d27] rounded-2xl border border-[#2a2d3a] p-8 max-w-md text-center">
          <AlertCircle className="w-14 h-14 text-yellow-400 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-white mb-2">Access Error</h1>
          <p className="text-gray-400 text-sm">{accessState.error}</p>
        </div>
      </div>
    );
  }
  if (!accessState.isAdmin) return <NotAuthorizedScreen userEmail={accessState.userEmail} />;

  // ===========================================================================
  // TABLE COLUMNS
  // ===========================================================================
  const txColumns = [
    { header: 'Date', key: 'created_at', render: (r) => formatDate(r?.created_at) },
    { header: 'Type', key: 'type' },
    { header: 'Status', key: 'status', render: (r) => <StatusBadge status={r?.status} /> },
    { header: 'Gross', key: 'gross_amount', render: (r) => formatCurrency(r?.gross_amount) },
    { header: 'Fee', key: 'platform_fee', render: (r) => formatCurrency(r?.platform_fee) },
    { header: 'Creator', key: 'creator_amount', render: (r) => formatCurrency(r?.creator_amount) },
    { header: 'User', key: 'user_email' },
    { header: 'Creator Email', key: 'creator_email' }
  ];

  const creatorColumns = [
    { header: 'Email', key: 'email' },
    { header: 'Name', key: 'name' },
    { header: 'Commission', key: 'commission_rate', render: (r) => r?.commission_rate ? `${r.commission_rate}%` : '\u2014' },
    { header: 'Country', key: 'country' },
    { header: 'Connect ID', key: 'stripe_connect_account_id', render: (r) => r?.stripe_connect_account_id ? `${r.stripe_connect_account_id.substring(0, 12)}...` : '\u2014' },
    { header: 'Charges', key: 'stripe_charges_enabled', render: (r) => <span className={r?.stripe_charges_enabled ? 'text-emerald-400' : 'text-red-400'}>{r?.stripe_charges_enabled ? 'Yes' : 'No'}</span> },
    { header: 'Payouts', key: 'stripe_payouts_enabled', render: (r) => <span className={r?.stripe_payouts_enabled ? 'text-emerald-400' : 'text-red-400'}>{r?.stripe_payouts_enabled ? 'Yes' : 'No'}</span> },
    { header: 'Joined', key: 'created_at', render: (r) => formatDate(r?.created_at) }
  ];

  const subColumns = [
    { header: 'User', key: 'user_email' },
    { header: 'Creator', key: 'creator_email' },
    { header: 'Status', key: 'status', render: (r) => <StatusBadge status={r?.status} /> },
    { header: 'Price', key: 'price_amount', render: (r) => formatCurrency(r?.price_amount) },
    { header: 'Period End', key: 'current_period_end', render: (r) => formatDate(r?.current_period_end) },
    { header: 'Stripe ID', key: 'stripe_subscription_id', render: (r) => r?.stripe_subscription_id ? `${r.stripe_subscription_id.substring(0, 12)}...` : '\u2014' },
    { header: 'Created', key: 'created_at', render: (r) => formatDate(r?.created_at) }
  ];

  const userColumns = [
    { header: 'Name', key: 'name', render: (r) => r?.name || '\u2014' },
    { header: 'Email', key: 'email', render: (r) => r?.email || '\u2014' },
    { header: 'Role', key: 'is_creator', render: (r) => (
      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${r?.is_admin ? 'bg-red-500/20 text-red-300' : r?.is_creator ? 'bg-blue-500/20 text-blue-300' : 'bg-gray-500/20 text-gray-300'}`}>
        {r?.is_admin ? 'Admin' : r?.is_creator ? 'Creator' : 'User'}
      </span>
    )},
    { header: 'Bio', key: 'bio', render: (r) => r?.bio ? (r.bio.length > 40 ? r.bio.substring(0, 40) + '...' : r.bio) : '\u2014' },
    { header: 'Joined', key: 'created_at', render: (r) => formatDate(r?.created_at) }
  ];

  const tabs = [
    { id: 'overview', label: 'Overview', icon: Activity },
    { id: 'transactions', label: 'Transactions', icon: CreditCard },
    { id: 'creators', label: 'Creators', icon: Users },
    { id: 'commissions', label: 'Commissions', icon: ShieldCheck },
    { id: 'users', label: 'All Users', icon: Shield },
    { id: 'subscriptions', label: 'Subscriptions', icon: TrendingUp }
  ];

  // ===========================================================================
  // RENDER
  // ===========================================================================
  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#0f1117] text-white">
      {/* Header */}
      <header className="bg-[#13151c] border-b border-[#2a2d3a] sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div>
              <h1 className="text-lg font-bold text-white">Admin Dashboard</h1>
              <p className="text-xs text-gray-500 mt-0.5">
                {accessState.userEmail && `${accessState.userEmail}`}
                {lastUpdated && ` \u00b7 Updated ${lastUpdated.toLocaleTimeString()}`}
              </p>
            </div>
            <button
              data-testid="refresh-dashboard-btn"
              onClick={handleRefresh}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition text-sm font-medium"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 -mb-px overflow-x-auto">
            {tabs.map((tab) => {
              const TabIcon = tab.icon;
              return (
                <button
                  key={tab.id}
                  data-testid={`tab-${tab.id}`}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-1.5 px-4 py-3 text-xs font-medium border-b-2 transition whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-400'
                      : 'border-transparent text-gray-500 hover:text-gray-300'
                  }`}
                >
                  <TabIcon className="w-3.5 h-3.5" />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {metricsError && (
          <ErrorBanner error={`Data source error: ${metricsError}`} onDismiss={() => setMetricsError(null)} />
        )}

        {/* OVERVIEW TAB */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* KPI Grid */}
            <div data-testid="kpi-grid" className="grid grid-cols-2 lg:grid-cols-3 gap-4">
              <KPICard testId="kpi-total-users" title="Real Users" value={metrics?.total_users} icon={Users} color="blue" loading={loading} error={typeof metrics?.total_users === 'object'} subtitle={metrics?.internal_accounts > 0 ? `${metrics.internal_accounts} internal/test excluded` : undefined} />
              <KPICard testId="kpi-total-creators" title="Payment-Ready Creators" value={metrics?.total_creators} icon={TrendingUp} color="purple" loading={loading} error={typeof metrics?.total_creators === 'object'} subtitle={metrics?.real_creators_all > 0 ? `${metrics.real_creators_all} total real creators (${metrics.real_creators_all - (metrics.payment_ready_creators || 0)} not Stripe-connected)` : undefined} />
              <KPICard testId="kpi-active-subs" title="Active Subscriptions" value={metrics?.active_subscriptions} icon={CreditCard} color="green" loading={loading} error={typeof metrics?.active_subscriptions === 'object'} subtitle={metrics?.cancelling_subscriptions > 0 ? `${metrics.cancelling_subscriptions} cancelling` : undefined} />
              <KPICard testId="kpi-total-revenue" title="Total Revenue" value={metrics?.total_revenue} icon={DollarSign} color="emerald" isCurrency loading={loading} error={typeof metrics?.total_revenue === 'object'} />
              <KPICard testId="kpi-platform-fees" title="Platform Fees" value={metrics?.platform_fees} icon={PiggyBank} color="orange" isCurrency loading={loading} error={typeof metrics?.platform_fees === 'object'} />
              <KPICard testId="kpi-creator-transfers" title="Creator Transfers" value={metrics?.creator_transfers} icon={DollarSign} color="teal" isCurrency loading={loading} error={typeof metrics?.creator_transfers === 'object'} />
            </div>

            {/* Charts Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <ChartCard
                testId="chart-transactions"
                title="Transaction Volume (30 days)"
                data={charts.transactions}
                dataKey="count"
                secondaryKey="completed"
                color="#3B82F6"
                secondaryColor="#60A5FA"
                type="bar"
              />
              <ChartCard
                testId="chart-users"
                title="New Users (30 days)"
                data={charts.users}
                dataKey="users"
                secondaryKey="creators"
                color="#8B5CF6"
                secondaryColor="#A78BFA"
                type="bar"
              />
              <ChartCard
                testId="chart-subscriptions"
                title="Subscriptions (30 days)"
                data={charts.subscriptions}
                dataKey="total"
                secondaryKey="active"
                color="#10B981"
                secondaryColor="#34D399"
                type="bar"
              />
              <ChartCard
                testId="chart-revenue"
                title="Revenue by Day (30 days)"
                data={charts.transactions}
                dataKey="volume"
                color="#F59E0B"
                isCurrency
                type="area"
              />
            </div>

            {/* Admin Tools — small operational utilities. Currently houses the
                Referral Notification Reconciler. Add additional read/idempotent
                tools here as needed; never expose mutation-heavy ops without a
                dry-run + confirm gate. */}
            <ReferralReconcileTool token={token} />
          </div>
        )}

        {/* TRANSACTIONS TAB */}
        {activeTab === 'transactions' && (
          <div className="bg-[#1a1d27] rounded-xl border border-[#2a2d3a] p-5">
            <h3 className="text-sm font-semibold text-white mb-4">Transactions</h3>
            <Filters
              testId="tx-filters"
              filters={[
                { key: 'status', label: 'Status', type: 'select', options: [
                  { value: 'completed', label: 'Completed' },
                  { value: 'pending', label: 'Pending' },
                  { value: 'abandoned', label: 'Abandoned' },
                  { value: 'failed', label: 'Failed' },
                  { value: 'refunded', label: 'Refunded' },
                  { value: 'all', label: 'All (incl. abandoned)' }
                ]},
                { key: 'type', label: 'Type', type: 'select', options: [
                  { value: 'tip', label: 'Tip' },
                  { value: 'subscription', label: 'Subscription' },
                  { value: 'workout_plan', label: 'Workout Plan' },
                  { value: 'meal_plan', label: 'Meal Plan' },
                  { value: 'one_time', label: 'One Time' }
                ]}
              ]}
              values={txFilters}
              onChange={(key, value) => { setTxFilters(prev => ({ ...prev, [key]: value })); setTxOffset(0); }}
              onClear={() => { setTxFilters({ status: 'completed' }); setTxOffset(0); }}
            />
            <DataTable
              testId="tx-table"
              columns={txColumns}
              data={transactions.data}
              loading={transactionsLoading}
              error={transactionsError}
              total={transactions.total}
              limit={limit}
              offset={txOffset}
              onPageChange={setTxOffset}
              onExport={() => handleExport('transactions')}
            />
          </div>
        )}

        {/* CREATORS TAB */}
        {activeTab === 'creators' && (
          <div className="bg-[#1a1d27] rounded-xl border border-[#2a2d3a] p-5">
            <h3 className="text-sm font-semibold text-white mb-4">Creators</h3>
            <Filters
              testId="creators-filters"
              filters={[
                { key: 'connected', label: 'Stripe Connected', type: 'select', options: [
                  { value: 'true', label: 'Connected' },
                  { value: 'false', label: 'Not Connected' }
                ]}
              ]}
              values={creatorsFilters}
              onChange={(key, value) => {
                const boolValue = value === '' ? undefined : value === 'true';
                setCreatorsFilters(prev => ({ ...prev, [key]: boolValue }));
                setCreatorsOffset(0);
              }}
              onClear={() => { setCreatorsFilters({}); setCreatorsOffset(0); }}
            />
            <DataTable
              testId="creators-table"
              columns={creatorColumns}
              data={creators.data}
              loading={creatorsLoading}
              error={creatorsError}
              total={creators.total}
              limit={limit}
              offset={creatorsOffset}
              onPageChange={setCreatorsOffset}
              onExport={() => handleExport('creators')}
            />
          </div>
        )}

        {/* SUBSCRIPTIONS TAB */}
        {activeTab === 'subscriptions' && (
          <div className="bg-[#1a1d27] rounded-xl border border-[#2a2d3a] p-5">
            <h3 className="text-sm font-semibold text-white mb-4">Subscriptions</h3>
            <Filters
              testId="subs-filters"
              filters={[
                { key: 'status', label: 'Status', type: 'select', options: [
                  { value: 'active', label: 'Active' },
                  { value: 'cancelling', label: 'Cancelling' },
                  { value: 'cancelled', label: 'Cancelled' },
                  { value: 'canceled', label: 'Cancelled (Stripe)' },
                  { value: 'past_due', label: 'Past Due' },
                  { value: 'expired', label: 'Expired' }
                ]}
              ]}
              values={subsFilters}
              onChange={(key, value) => { setSubsFilters(prev => ({ ...prev, [key]: value })); setSubsOffset(0); }}
              onClear={() => { setSubsFilters({}); setSubsOffset(0); }}
            />
            <DataTable
              testId="subs-table"
              columns={subColumns}
              data={subscriptions.data}
              loading={subscriptionsLoading}
              error={subscriptionsError}
              total={subscriptions.total}
              limit={limit}
              offset={subsOffset}
              onPageChange={setSubsOffset}
              onExport={() => handleExport('subscriptions')}
            />
          </div>
        )}
        {/* COMMISSIONS TAB (R2 admin override UI) */}
        {activeTab === 'commissions' && (
          <AdminCommissionOverrides token={token} />
        )}
        {/* USERS TAB */}
        {activeTab === 'users' && (
          <div className="bg-[#1a1d27] rounded-xl border border-[#2a2d3a] p-5">
            <h3 className="text-sm font-semibold text-white mb-4">All Users ({allUsers.total})</h3>
            <DataTable
              testId="users-table"
              columns={userColumns}
              data={allUsers.data}
              loading={allUsersLoading}
              error={allUsersError}
              total={allUsers.total}
              limit={limit}
              offset={usersOffset}
              onPageChange={setUsersOffset}
              onExport={() => handleExport('users')}
            />
          </div>
        )}
      </main>
    </div>
  );
};

// =============================================================================
// REFERRAL NOTIFICATION RECONCILE TOOL (admin-only utility, Overview tab)
// =============================================================================
// Two-step UX:
//   1. Click "Dry run" → call POST /api/admin/referrals/reconcile-notifications
//      with {dry_run: true}. Shows scanned + would_create counts + sample.
//   2. Click "Apply" → re-confirms via inline confirmation banner, then calls
//      the same endpoint with {dry_run: false}. Shows created counts.
// Never auto-applies. Apply is gated behind an explicit "Confirm apply" click
// that only appears AFTER a successful dry-run.
// Endpoint already enforces admin-only (403 for non-admin) so this is also a
// defense-in-depth UI gate, not the only gate.
const ReferralReconcileTool = ({ token }) => {
  const [loading, setLoading] = useState(false);
  const [dryResult, setDryResult] = useState(null);
  const [applyResult, setApplyResult] = useState(null);
  const [error, setError] = useState(null);
  const [confirmingApply, setConfirmingApply] = useState(false);

  const call = async (dryRun) => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/admin/referrals/reconcile-notifications`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token ? `Bearer ${token}` : '',
        },
        body: JSON.stringify({ dry_run: dryRun }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || `HTTP ${res.status}`);
      if (dryRun) {
        setDryResult(data);
        setApplyResult(null);
        setConfirmingApply(false);
      } else {
        setApplyResult(data);
        setConfirmingApply(false);
      }
    } catch (e) {
      setError(e.message || 'Reconcile call failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="rounded-xl border border-[#2a2d3a] bg-[#13151c] p-5"
      data-testid="admin-referral-reconcile-tool"
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <h3 className="text-sm font-semibold text-white">Referral Notification Reconciler</h3>
          <p className="text-xs text-gray-400 mt-1 max-w-2xl">
            Backfill in-app notifications for qualified referrals that pre-date the
            notification fix. Read-only by default (dry run). Idempotent — re-running
            yields zero new rows. Does NOT change commissions, rewards, or payment data.
          </p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-3">
        <button
          data-testid="admin-reconcile-dryrun-btn"
          onClick={() => call(true)}
          disabled={loading}
          className="px-3 py-1.5 text-xs font-medium bg-[#1f2330] border border-[#2a2d3a] hover:bg-[#262a38] text-gray-200 rounded-md disabled:opacity-50 transition"
        >
          {loading ? 'Running…' : 'Dry run'}
        </button>
        {dryResult && dryResult.would_create?.total > 0 && !applyResult && !confirmingApply && (
          <button
            data-testid="admin-reconcile-apply-btn"
            onClick={() => setConfirmingApply(true)}
            className="px-3 py-1.5 text-xs font-medium bg-emerald-700 hover:bg-emerald-600 text-white rounded-md transition"
          >
            Apply ({dryResult.would_create.total})
          </button>
        )}
        {confirmingApply && (
          <>
            <span className="text-xs text-amber-400 self-center">Create {dryResult.would_create.total} notification(s) — confirm?</span>
            <button
              data-testid="admin-reconcile-confirm-btn"
              onClick={() => call(false)}
              disabled={loading}
              className="px-3 py-1.5 text-xs font-medium bg-emerald-600 hover:bg-emerald-500 text-white rounded-md disabled:opacity-50 transition"
            >
              Confirm apply
            </button>
            <button
              onClick={() => setConfirmingApply(false)}
              disabled={loading}
              className="px-3 py-1.5 text-xs font-medium bg-[#1f2330] border border-[#2a2d3a] text-gray-300 hover:bg-[#262a38] rounded-md disabled:opacity-50 transition"
            >
              Cancel
            </button>
          </>
        )}
      </div>

      {error && (
        <div className="rounded-md border border-red-800 bg-red-900/30 px-3 py-2 text-xs text-red-300 mb-2" data-testid="admin-reconcile-error">
          {error}
        </div>
      )}

      {dryResult && !applyResult && (
        <div className="rounded-md border border-[#2a2d3a] bg-[#0f1117] p-3 text-xs text-gray-300 space-y-2" data-testid="admin-reconcile-dryrun-result">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <Stat label="Scanned" value={dryResult.scanned} />
            <Stat label="Skipped (existing)" value={dryResult.skipped_existing} />
            <Stat label="Would create — unlocked" value={dryResult.would_create?.referral_reward_unlocked || 0} />
            <Stat label="Would create — qualified" value={dryResult.would_create?.referral_invitee_qualified || 0} />
          </div>
          {Array.isArray(dryResult.sample_actions) && dryResult.sample_actions.length > 0 && (
            <details className="text-[11px] text-gray-400">
              <summary className="cursor-pointer hover:text-gray-200">Sample (first {dryResult.sample_actions.length})</summary>
              <pre className="mt-2 bg-black/40 p-2 rounded text-[10px] overflow-auto max-h-56">{JSON.stringify(dryResult.sample_actions, null, 2)}</pre>
            </details>
          )}
        </div>
      )}

      {applyResult && (
        <div className="rounded-md border border-emerald-800 bg-emerald-900/30 px-3 py-2 text-xs text-emerald-200" data-testid="admin-reconcile-apply-result">
          <p className="font-semibold">Reconcile complete.</p>
          <p className="mt-1 opacity-90">
            Scanned {applyResult.scanned} · Skipped {applyResult.skipped_existing} · Created {applyResult.created?.total ?? 0}{' '}
            ({applyResult.created?.referral_reward_unlocked || 0} unlocked, {applyResult.created?.referral_invitee_qualified || 0} qualified)
          </p>
        </div>
      )}
    </div>
  );
};

const Stat = ({ label, value }) => (
  <div className="rounded bg-[#1a1d29] px-2 py-1.5 border border-[#2a2d3a]">
    <p className="text-[10px] text-gray-500 uppercase tracking-wide">{label}</p>
    <p className="text-sm font-semibold text-white mt-0.5">{value ?? 0}</p>
  </div>
);

// =============================================================================
// WRAPPED EXPORT
// =============================================================================
const AdminDashboard = (props) => (
  <AdminErrorBoundary>
    <AdminDashboardContent {...props} />
  </AdminErrorBoundary>
);

export default AdminDashboard;
